import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/apiClient";
import { complianceCheck, REQUIRED_FIELDS } from "../lib/roles";
import { buildPlainText } from "./NoteEditor";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  FileText, Search, CheckCircle2, AlertTriangle, Copy, Download, ShieldCheck, Users, FileText as FilePdf, BadgeCheck, Loader2, Flag,
} from "lucide-react";

const ADMIN_ROW = [
  ["Date of Service", "date_of_service"],
  ["Service", "service_name"],
  ["Type of Contact", "type_of_contact"],
  ["Place of Service", "place_of_service"],
  ["Duration (min)", "duration_minutes"],
  ["Units", "units"],
  ["Member Name", "member_name"],
  ["Member ID / Record #", "member_id"],
];
const NARR = [
  ["Targeted PCP Goal / Objective", "goals_addressed"],
  ["Staff Interventions Provided", "interventions"],
  ["Member Response", "member_response"],
  ["Progress", "progress"],
  ["Plan for Next Week", "plan"],
];

export default function OversightView({ mode, canReview }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const [reviewing, setReviewing] = useState(false);
  const [awaitingOnly, setAwaitingOnly] = useState(false);
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [selIds, setSelIds] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);

  const toggleSel = (id) =>
    setSelIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const bulkSignOff = async () => {
    if (!selIds.length) return;
    setBulkBusy(true);
    try {
      await api.post("/notes/review/bulk", { note_ids: selIds, reviewed: true });
      const by = "you";
      setNotes((ns) =>
        ns.map((n) =>
          selIds.includes(n.id)
            ? { ...n, status: "reviewed", reviewed_by: n.reviewed_by || by, reviewed_at: new Date().toISOString() }
            : n
        )
      );
      toast.success(`Signed off ${selIds.length} note(s).`);
      setSelIds([]);
    } catch {
      toast.error("Could not bulk sign off.");
    } finally {
      setBulkBusy(false);
    }
  };

  useEffect(() => {
    api
      .get("/notes/all")
      .then((res) => {
        setNotes(res.data);
        if (res.data.length) setActive(res.data[0]);
      })
      .catch(() => toast.error("Could not load notes."))
      .finally(() => setLoading(false));
  }, []);

  const review = async (note, reviewed) => {
    setReviewing(true);
    try {
      const { data } = await api.post(`/notes/${note.id}/review`, { reviewed });
      const merged = { ...note, ...data };
      setNotes((ns) => ns.map((n) => (n.id === note.id ? merged : n)));
      setActive((a) => (a && a.id === note.id ? merged : a));
      toast.success(reviewed ? "Signed off — note marked Reviewed." : "Sign-off removed.");
    } catch (e) {
      toast.error("Could not update sign-off.");
    } finally {
      setReviewing(false);
    }
  };

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    let list = notes;
    if (awaitingOnly) list = list.filter((n) => n.status !== "reviewed");
    if (flaggedOnly) list = list.filter((n) => n.goal_flagged);
    if (!t) return list;
    return list.filter((n) => {
      const s = n.structured || {};
      return (
        (s.member_name || "").toLowerCase().includes(t) ||
        (n.staff_name || "").toLowerCase().includes(t)
      );
    });
  }, [q, notes, awaitingOnly, flaggedOnly]);

  const stats = useMemo(() => {
    const total = notes.length;
    const complete = notes.filter((n) => complianceCheck(n.structured).complete).length;
    const awaiting = notes.filter((n) => n.status !== "reviewed").length;
    return { total, complete, incomplete: total - complete, awaiting };
  }, [notes]);

  const isCompliance = mode === "compliance";

  const exportSimplePractice = async () => {
    try {
      const res = await api.get("/notes/export/simplepractice", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "psr-notes-simplepractice.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV exported for SimplePractice.");
    } catch {
      toast.error("Could not export.");
    }
  };

  const exportNote = (n) => {
    const blob = new Blob([buildPlainText(n.structured, n.show_timestamp !== false, n.created_at)], {
      type: "text/plain",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `PSR-${n.staff_name || "staff"}-${n.structured?.member_name || "member"}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyNote = async (n) => {
    const text = buildPlainText(n.structured, n.show_timestamp !== false, n.created_at);
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard!");
    } catch {
      toast.error("Copy not permitted here. Use Export instead.");
    }
  };

  const pdfNote = async (n) => {
    try {
      const res = await api.post(
        "/notes/pdf",
        {
          structured: n.structured,
          show_timestamp: n.show_timestamp !== false,
          created_at: n.created_at,
          status: n.status,
          reviewed_by: n.reviewed_by,
          reviewed_at: n.reviewed_at,
        },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `PSR-${n.staff_name || "staff"}-${n.structured?.member_name || "member"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded!");
    } catch {
      toast.error("Could not build PDF.");
    }
  };

  return (
    <div data-testid="oversight-view">
      {/* header + stat cards */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white border border-[#E2DFD8] flex items-center justify-center">
            {isCompliance ? (
              <ShieldCheck className="w-5 h-5 text-[#C26D5C]" strokeWidth={1.5} />
            ) : (
              <Users className="w-5 h-5 text-[#4A6B53]" strokeWidth={1.5} />
            )}
          </div>
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-[#121814]">
              {isCompliance ? "Compliance Review" : "Team Notes"}
            </h1>
            <p className="text-sm text-[#7A8C82]">
              {isCompliance
                ? "Read-only audit of every note. Flags missing billable fields."
                : "Review notes written by your team. Approve with one tap."}
            </p>
          </div>
        </div>
        <button
          data-testid="export-simplepractice-btn"
          onClick={exportSimplePractice}
          className="flex items-center gap-2 h-10 px-4 rounded-full bg-white border border-[#E2DFD8] text-sm font-medium text-[#4A6B53] hover:border-[#4A6B53] transition-colors"
        >
          <Download className="w-4 h-4" strokeWidth={1.5} /> Export for SimplePractice
        </button>
      </div>

      <div className={`grid ${canReview ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-3"} gap-4 mb-6`}>
        {[
          { label: "Total Notes", value: stats.total, color: "#4A6B53", show: true },
          { label: "Billable-Ready", value: stats.complete, color: "#4A6B53", show: true },
          { label: "Needs Attention", value: stats.incomplete, color: "#C26D5C", show: true },
          { label: "Awaiting Sign-Off", value: stats.awaiting, color: "#D4A373", show: canReview, testid: "stat-awaiting-signoff" },
        ].filter((c) => c.show).map((c) => (
          <div key={c.label} data-testid={c.testid} className="bg-white border border-[#E2DFD8] rounded-xl p-4 shadow-sm">
            <p className="text-[10px] font-bold text-[#7A8C82] uppercase tracking-wider">{c.label}</p>
            <p className="mt-1 font-display text-3xl font-extrabold" style={{ color: c.color }}>
              {c.value}
            </p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* list */}
        <div className="col-span-12 lg:col-span-5">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#7A8C82]" strokeWidth={1.5} />
            <Input
              data-testid="oversight-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search member or staff…"
              className="pl-9 h-11 bg-white border-[#E2DFD8]"
            />
          </div>

          <div className="flex flex-wrap gap-2 mb-3">
            {canReview && (
              <button
                data-testid="awaiting-signoff-filter"
                onClick={() => setAwaitingOnly((v) => !v)}
                className={`flex items-center gap-2 h-9 px-3 rounded-full text-xs font-semibold border transition-colors ${
                  awaitingOnly ? "bg-[#4A6B53] border-[#3D5A45] text-white" : "bg-white border-[#E2DFD8] text-[#7A8C82] hover:border-[#4A6B53]"
                }`}
              >
                <BadgeCheck className="w-3.5 h-3.5" strokeWidth={2} />
                {awaitingOnly ? "Awaiting sign-off" : "Awaiting sign-off"}
              </button>
            )}
            <button
              data-testid="flagged-only-filter"
              onClick={() => setFlaggedOnly((v) => !v)}
              className={`flex items-center gap-2 h-9 px-3 rounded-full text-xs font-semibold border transition-colors ${
                flaggedOnly ? "bg-[#C26D5C] border-[#a9553f] text-white" : "bg-white border-[#E2DFD8] text-[#7A8C82] hover:border-[#C26D5C]"
              }`}
            >
              <Flag className="w-3.5 h-3.5" strokeWidth={2} />
              Goal-check flagged
            </button>
          </div>

          {canReview && (
            <div className="mb-3 flex items-center justify-between gap-2 flex-wrap">
              <button
                data-testid="select-all-awaiting-btn"
                onClick={() => setSelIds(filtered.filter((n) => n.status !== "reviewed").map((n) => n.id))}
                className="text-xs font-semibold text-[#4A6B53] hover:underline"
              >
                Select all awaiting
              </button>
              {selIds.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[#7A8C82]">{selIds.length} selected</span>
                  <button
                    data-testid="bulk-signoff-btn"
                    onClick={bulkSignOff}
                    disabled={bulkBusy}
                    className="flex items-center gap-1.5 h-8 px-3 rounded-full bg-[#4A6B53] hover:bg-[#3D5A45] text-white text-xs font-semibold"
                  >
                    {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BadgeCheck className="w-3.5 h-3.5" strokeWidth={2} />}
                    Approve selected
                  </button>
                  <button data-testid="bulk-clear-btn" onClick={() => setSelIds([])} className="text-xs text-[#7A8C82] hover:text-[#C26D5C]">Clear</button>
                </div>
              )}
            </div>
          )}

          {loading && <p className="text-sm text-[#7A8C82] px-1">Loading…</p>}
          {!loading && filtered.length === 0 && (
            <p className="text-sm text-[#7A8C82] px-1">No notes found.</p>
          )}

          <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
            {filtered.map((n) => {
              const s = n.structured || {};
              const chk = complianceCheck(s);
              const isActive = active?.id === n.id;
              return (
                <div
                  key={n.id}
                  className={`flex items-center gap-2 p-3.5 rounded-xl border transition-colors ${
                    isActive ? "bg-[#DCE5DF] border-[#4A6B53]" : "bg-white border-[#E2DFD8] hover:bg-[#F4F3EF]"
                  }`}
                >
                  {canReview && (
                    <button
                      data-testid={`select-note-${n.id}`}
                      onClick={(e) => { e.stopPropagation(); toggleSel(n.id); }}
                      className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                        selIds.includes(n.id) ? "bg-[#4A6B53] border-[#4A6B53]" : "border-[#C9C6BE] bg-white"
                      }`}
                    >
                      {selIds.includes(n.id) && <CheckCircle2 className="w-3 h-3 text-white" strokeWidth={3} />}
                    </button>
                  )}
                  <div
                    data-testid={`oversight-item-${n.id}`}
                    onClick={() => setActive(n)}
                    className="flex-1 min-w-0 text-left cursor-pointer"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-[#121814] truncate">
                          {s.member_name || "Unnamed member"}
                        </p>
                        <p className="text-xs text-[#7A8C82] truncate">
                          {n.staff_name} · {n.staff_credentials || n.staff_role} ·{" "}
                          {s.date_of_service || new Date(n.updated_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        {n.goal_flagged && (
                          <span data-testid={`oversight-flag-${n.id}`} className="flex items-center gap-1 text-[10px] font-bold uppercase text-[#C26D5C]">
                            <Flag className="w-3.5 h-3.5" strokeWidth={2} /> Goal check
                          </span>
                        )}
                        {n.status === "reviewed" && (
                          <span className="flex items-center gap-1 text-[10px] font-bold uppercase text-[#4A6B53]">
                            <BadgeCheck className="w-3.5 h-3.5" strokeWidth={2} /> Reviewed
                          </span>
                        )}
                        {chk.complete ? (
                          <span className="flex items-center gap-1 text-[10px] font-bold uppercase text-[#4A6B53]">
                            <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2} /> Ready
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-[10px] font-bold uppercase text-[#C26D5C]">
                            <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2} /> {chk.missing.length} missing
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* detail */}
        <div className="col-span-12 lg:col-span-7">
          {active ? (
            <ReadOnlyNote
              note={active}
              onCopy={() => copyNote(active)}
              onExport={() => exportNote(active)}
              onPdf={() => pdfNote(active)}
              canReview={canReview}
              reviewing={reviewing}
              onReview={(reviewed) => review(active, reviewed)}
            />
          ) : (
            <div className="flex items-center justify-center h-64 text-sm text-[#7A8C82] bg-white border border-[#E2DFD8] rounded-2xl">
              Select a note to review.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ReadOnlyNote({ note, onCopy, onExport, onPdf, canReview, onReview, reviewing }) {
  const s = note.structured || {};
  const chk = complianceCheck(s);
  const isReviewed = note.status === "reviewed";
  return (
    <div className="bg-white border border-[#E2DFD8] rounded-2xl shadow-sm overflow-hidden fade-up" data-testid="oversight-detail">
      {/* compliance banner */}
      <div
        className={`px-6 py-4 flex items-center justify-between gap-3 ${
          chk.complete ? "bg-[#DCE5DF]" : "bg-[#F6E4DF]"
        }`}
      >
        <div className="flex items-center gap-2.5">
          {chk.complete ? (
            <CheckCircle2 className="w-5 h-5 text-[#4A6B53]" strokeWidth={1.5} />
          ) : (
            <AlertTriangle className="w-5 h-5 text-[#C26D5C]" strokeWidth={1.5} />
          )}
          <div>
            <p className="text-sm font-bold text-[#121814]">
              {chk.complete ? "Billable-ready" : `${chk.missing.length} required field(s) missing`}
            </p>
            <p className="text-xs text-[#7A8C82]">
              {chk.filled}/{chk.total} required fields complete
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="oversight-copy-btn" onClick={onCopy} variant="outline" size="sm" className="rounded-full border-[#E2DFD8] bg-white">
            <Copy className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Copy
          </Button>
          <Button data-testid="oversight-export-btn" onClick={onExport} variant="outline" size="sm" className="rounded-full border-[#E2DFD8] bg-white">
            <Download className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Export
          </Button>
          <Button data-testid="oversight-pdf-btn" onClick={onPdf} variant="outline" size="sm" className="rounded-full border-[#E2DFD8] bg-white">
            <FilePdf className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> PDF
          </Button>
        </div>
      </div>

      {/* QP sign-off bar */}
      {canReview && (
        <div className="px-6 py-3 border-b border-[#E2DFD8] flex items-center justify-between gap-3 bg-[#F9F9F6]">
          {isReviewed ? (
            <p className="text-xs text-[#4A6B53] font-medium flex items-center gap-1.5">
              <BadgeCheck className="w-4 h-4" strokeWidth={1.5} />
              Signed off by {note.reviewed_by}
              {note.reviewed_at ? ` · ${new Date(note.reviewed_at).toLocaleDateString()}` : ""}
            </p>
          ) : (
            <p className="text-xs text-[#7A8C82]">This note is awaiting QP sign-off.</p>
          )}
          <Button
            data-testid="qp-signoff-btn"
            onClick={() => onReview(!isReviewed)}
            disabled={reviewing}
            size="sm"
            className={`rounded-full text-white ${isReviewed ? "bg-[#7A8C82] hover:bg-[#67766d]" : "bg-[#4A6B53] hover:bg-[#3D5A45]"}`}
          >
            {reviewing ? <Loader2 className="w-4 h-4 animate-spin" /> : isReviewed ? "Undo sign-off" : (<><BadgeCheck className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Approve & sign off</>)}
          </Button>
        </div>
      )}

      {isReviewed && !canReview && (
        <div className="px-6 py-2.5 border-b border-[#E2DFD8] bg-[#F4F6F3]">
          <p className="text-xs text-[#4A6B53] font-medium flex items-center gap-1.5">
            <BadgeCheck className="w-4 h-4" strokeWidth={1.5} /> QP signed off by {note.reviewed_by}
          </p>
        </div>
      )}

      {!chk.complete && (
        <div className="px-6 py-3 border-b border-[#E2DFD8] flex flex-wrap gap-2">
          {chk.missing.map((m) => (
            <span key={m.key} className="text-[11px] font-medium px-2 py-1 rounded-md bg-[#F6E4DF] text-[#a9553f]">
              Missing: {m.label}
            </span>
          ))}
        </div>
      )}

      <div className="p-6">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3 mb-5">
          {ADMIN_ROW.map(([label, key]) => (
            <div key={key}>
              <p className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider">{label}</p>
              <p className="text-sm text-[#121814] mt-0.5 break-words">{s[key] || <span className="text-[#C26D5C]">—</span>}</p>
            </div>
          ))}
          <div className="col-span-2">
            <p className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider">Staff</p>
            <p className="text-sm text-[#121814] mt-0.5">
              {s.staff_name || note.staff_name}
              {s.staff_credentials ? `, ${s.staff_credentials}` : ""}
            </p>
          </div>
        </div>
        <div className="space-y-4 border-t border-[#E2DFD8] pt-5">
          {NARR.map(([label, key]) => (
            <div key={key}>
              <p className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider mb-1">{label}</p>
              <p className="text-sm text-[#3A453F] leading-relaxed whitespace-pre-wrap">
                {s[key] || <span className="text-[#C26D5C]">Not documented</span>}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
